import type { SyntheticEvent } from "react";
import React from "react";
import type { MODAL_USC_MOBILE_NAV_CTA } from "../../../utils/modalConstants";
import { UscLabel, useUscLabel, useUscOptionalLabel } from "../../../utils/labels";
import { getMobileNavModalCtas } from "../../../utils/ctaUtils";
import * as Styles from "./MobileNavCtaModal.styles";
import type { ModalViewType } from "../../../../../common-deprecated/types/CommonTypes";
import PdfButton from "../common/basket/common/PdfButton";
import useNavParams from "../../../hooks/useNavParams";
import { DfVehicleStatus } from "../../../../shared-logic/utils/dfConstants";
import { useUscSelector } from "../../../redux/store";
import SaveToMyToyotaButton from "../common/basket/common/SaveToMyToyotaButton";
import ModalBaseView from "../styles/organisms/Modal";
import * as ModalStyles from "../styles/organisms/Modal.styles";
import Button from "../styles/atoms/Button";
import Text from "../styles/atoms/Text";
import Icon from "../styles/Icon";
import { trackCloseMobileNavModal, UscTrackingLocation } from "../../../utils/tracking";
import { getToyotaButtonVariant } from "../../../utils/pdpUtils";
import { VEHICLE_AGGREGATION_THRESHOLD } from "../../../../shared-logic/utils/uscUtils";

export type UscMobileNavCta = {
    type: typeof MODAL_USC_MOBILE_NAV_CTA;
};

const MobileNavCtaModal = (props: ModalViewType<UscMobileNavCta>): JSX.Element => {
    const { show, close, modalSettings } = props;

    const vehicle = useUscSelector((state) => state.vehicle);
    const vehicleAggregationsEnabled = useUscSelector((state) => state.uscSettings.enableVehicleAggregations);
    const showAdditionalCtasOnMobile = useUscSelector((state) => state.uscSettings.showAdditionalCtasOnMobile);
    const hasFewVehicles =
        useUscSelector((state) => state.vehicle.result?.vehicleCount ?? 0) < VEHICLE_AGGREGATION_THRESHOLD;

    const defaultTitle = useUscLabel("uscMobileNavCtaModalTitle");
    const reservedTitle = useUscLabel("uscPdpStatusReservedTitle", { model: vehicle.result?.product.model });
    const vehicleAggregationCountLabel = useUscLabel(
        hasFewVehicles ? "uscVehicleAggregationLess" : "uscVehicleAggregationMore",
    );
    const vehicleAggregationLabel = useUscLabel("uscVehicleAggregationCarsAvailable");

    const mobileNavCtaModalDescription = useUscOptionalLabel("uscMobileNavCtaModalDescription");

    const navParams = useNavParams(UscTrackingLocation.MobileNavModal);
    const { primary, secondary, tertiary } = getMobileNavModalCtas(navParams);

    // When the vehicle is reserved we show a different title in the modal
    // We reuse the label 'uscPdpStatusReservedTitle' which is originally used as the black reserved banner in the basket
    const title = vehicle.result?.vehicleStatus.code === DfVehicleStatus.Reserved ? reservedTitle : defaultTitle;

    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="fullwidthautoheight">
            <ModalStyles.Header noBorder>
                <Button
                    variant="control"
                    onClick={() => {
                        trackCloseMobileNavModal(vehicle.type);
                        close();
                    }}
                >
                    <Icon variant="close" />
                </Button>
            </ModalStyles.Header>
            <ModalStyles.Body noSpacing>
                <Styles.Wrapper>
                    <Styles.Content>
                        <Text tag="h2" variant="h4">
                            {title}
                        </Text>

                        <Styles.Actions>
                            {vehicleAggregationsEnabled && (
                                <Styles.VehicleAggregation>
                                    <Icon
                                        variant={
                                            hasFewVehicles
                                                ? "vehicle-aggregations-fewer-cars"
                                                : "vehicle-aggregations-multiple-cars"
                                        }
                                    />
                                    <div>
                                        <span>{vehicleAggregationCountLabel}</span>
                                        {` ${vehicleAggregationLabel}`}
                                    </div>
                                </Styles.VehicleAggregation>
                            )}

                            {primary && (
                                <Button
                                    variant={getToyotaButtonVariant(navParams.context)}
                                    size="fullwidth"
                                    onClick={(event: SyntheticEvent) => {
                                        if (primary.onClick) {
                                            // Trigger close first as this will close the mobile nav modal and the onClick might open another.
                                            close();
                                            primary.onClick(event);
                                        }
                                    }}
                                    href={primary.href}
                                    rel={primary.rel}
                                >
                                    {primary.label}
                                </Button>
                            )}
                            {secondary.map((secondaryCta) => (
                                <Button
                                    key={secondaryCta.label}
                                    variant="outline"
                                    size="fullwidth"
                                    href={secondaryCta.href}
                                    onClick={(event: SyntheticEvent) => {
                                        if (secondaryCta.onClick) {
                                            // Trigger close first as this will close the mobile nav modal and the onClick might open another.
                                            close();
                                            secondaryCta.onClick(event);
                                        }
                                    }}
                                    rel={secondaryCta.rel}
                                >
                                    {secondaryCta.label}
                                </Button>
                            ))}
                        </Styles.Actions>
                    </Styles.Content>
                    <Styles.TertiaryCtaWrapper>
                        <Styles.TertiaryHeader>
                            <Text tag="h3" variant="h6">
                                <UscLabel label="uscMobileNavCtaModalMoreInformationTitle" />
                            </Text>
                            {mobileNavCtaModalDescription && (
                                <Text tag="p" variant="body-small-book">
                                    {mobileNavCtaModalDescription}
                                </Text>
                            )}
                        </Styles.TertiaryHeader>
                        {tertiary && (
                            <Styles.TertiaryActions>
                                <Button
                                    variant="cta"
                                    href={tertiary.href}
                                    onClick={(event) => {
                                        if (tertiary.onClick) {
                                            // Trigger close first as this will close the mobile nav modal and the onClick might open another.
                                            close();
                                            tertiary.onClick(event);
                                        }
                                    }}
                                    rel={tertiary.rel}
                                >
                                    {/* <Icon variant={tertiary.icon} /> */}
                                    <div>
                                        <Icon variant="call-center" iconSize="medium" />
                                        {tertiary.label}
                                    </div>
                                    <Icon variant="arrow-right" />
                                </Button>
                            </Styles.TertiaryActions>
                        )}
                    </Styles.TertiaryCtaWrapper>
                    {showAdditionalCtasOnMobile && (
                        <Styles.AdditionalActions>
                            <PdfButton
                                buttonVariant="basket-cta"
                                trackingLocation={UscTrackingLocation.MobileNavModal}
                            />
                            <SaveToMyToyotaButton
                                buttonVariant="basket-cta"
                                trackingLocation={UscTrackingLocation.MobileNavModal}
                            />
                        </Styles.AdditionalActions>
                    )}
                </Styles.Wrapper>
            </ModalStyles.Body>
        </ModalBaseView>
    );
};

export default MobileNavCtaModal;
